import FacetFilterControls from '~/components/facet-filter/FacetFilterControls';
import { ProductCard } from '~/components/products/ProductCard';
import {
  paginationValidationSchema,
  translatePaginationFrom,
  translatePaginationTo,
} from '~/utils/pagination';
import { Pagination } from '~/components/Pagination';
import { NoResultsHint } from '~/components/products/NoResultsHint';
import { useRef } from 'react';
import { FacetFilterTracker } from '~/components/facet-filter/facet-filter-tracker';
import { filteredSearchLoaderFromPagination } from '~/utils/filtered-search-loader';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useSubmit } from '@remix-run/react';
import { ValidatedForm } from 'remix-validated-form';
import { withZod } from '@remix-validated-form/with-zod';
import { cn } from '~/utils/class-names';

export function FilterableProductGrid({
  result,
  resultWithoutFacetValueFilters,
  facetValueIds,
  appliedPaginationPage,
  appliedPaginationLimit,
  allowedPaginationLimits,
  mobileFiltersOpen,
  setMobileFiltersOpen,
  noResultsHeadline,
}: Awaited<
  ReturnType<
    ReturnType<
      typeof filteredSearchLoaderFromPagination
    >['filteredSearchLoader']
  >
> & {
  allowedPaginationLimits: Set<number>;
  mobileFiltersOpen: boolean;
  setMobileFiltersOpen: (arg0: boolean) => void;
  noResultsHeadline?: string;
}) {
  const { t } = useTranslation();
  const facetValuesTracker = useRef(new FacetFilterTracker());
  facetValuesTracker.current.update(
    result,
    resultWithoutFacetValueFilters,
    facetValueIds,
  );
  const hasFacetValues =
    facetValuesTracker.current.facetsWithValues.length > 0;

  const validator = withZod(
    paginationValidationSchema(allowedPaginationLimits),
  );
  const submit = useSubmit();

  return (
    <div className="mt-3 sm:mt-4 grid sm:grid-cols-5 gap-x-4">
      {hasFacetValues ? (
        <FacetFilterControls
          facetFilterTracker={facetValuesTracker.current}
          mobileFiltersOpen={mobileFiltersOpen}
          setMobileFiltersOpen={setMobileFiltersOpen}
        />
      ) : null}
      {result.items.length > 0 ? (
        <div
          className={cn(
            'sm:col-span-5 space-y-6',
            hasFacetValues ? 'lg:col-span-4' : 'lg:col-span-5',
          )}
        >
          <div className="grid grid-cols-2 gap-y-4 gap-x-3 md:grid-cols-3 lg:grid-cols-4 xl:gap-x-6">
            {result.items.map((item) => (
              <ProductCard key={item.productId} {...item} />
            ))}
          </div>

          <div className="flex flex-row justify-between items-center gap-4">
            <span className="self-start text-gray-500 text-sm mt-2">
              {t('product.showing')}{' '}
              {translatePaginationFrom(
                appliedPaginationPage,
                appliedPaginationLimit,
              )}{' '}
              {t('product.to')}{' '}
              {translatePaginationTo(
                appliedPaginationPage,
                appliedPaginationLimit,
                result.items.length,
              )}
            </span>
            <ValidatedForm
              validator={validator}
              method="get"
              onChange={(e) =>
                submit(e.currentTarget, { preventScrollReset: true })
              }
            >
              <Pagination
                appliedPaginationLimit={appliedPaginationLimit}
                allowedPaginationLimits={allowedPaginationLimits}
                totalItems={result.totalItems}
                appliedPaginationPage={appliedPaginationPage}
              />
            </ValidatedForm>
          </div>
        </div>
      ) : (
        <NoResultsHint
          facetFilterTracker={facetValuesTracker.current}
          headline={noResultsHeadline}
          className={cn(
            'sm:col-span-5 sm:p-4',
            hasFacetValues ? 'lg:col-span-4' : 'lg:col-span-5',
          )}
        />
      )}
    </div>
  );
}
